import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, Checkbox, Tooltip } from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { FieldArray } from 'react-final-form-arrays';
import { useField } from 'react-final-form';
import ItemsList from './ItemsList';
import useLocationName from '../../../../../hooks/useLocationName';
import { HoldingAccordionHeader } from './HoldingManagement';
import { shouldEnableVolumeManagement } from '../../../../../utils/configUtils';

function RenderHoldingItems({
  index,
  holding,
  template,
  controlledVocab,
  onEditItem,
  onUndoStackUpdate,
  holdingsFields, // Add holdings fields for HoldingAccordionHeader
  createUndoAction, // Add undo action creator
  // Batch operations props
  isSelected = false,
  onToggleSelection,
  isBatchModeActive = false,
}) {
  /** Get holding location id and name */
  const holdingLocationField = useField(
    `holdingsData[${index}].permanentLocationId`,
  );
  const holdingLocationId = holdingLocationField.input.value;
  const { locationName } = useLocationName(
    holdingLocationId,
    controlledVocab,
  );

  /** Get statistical code ids from instance to propagate to items */
  const instanceStatisticalCodeIdsField = useField('instanceData.statisticalCodeIds');
  const instanceStatisticalCodeIds = instanceStatisticalCodeIdsField?.input?.value || [];

  /** Get call number for holding accordion label */
  const callNumberField = useField(`holdingsData[${index}].callNumber`);

  /** Generate accordion label with location and call number */
  const handleAccordionLabel = () => {
    const location = holdingLocationId ? `[${locationName}]` : '';
    const callNumber = callNumberField.input.value
      ? ` > ${callNumberField.input.value}`
      : '';
    return location + callNumber;
  };

  const renderAccordionHeader = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          paddingLeft: '8px', // Add some padding to separate from accordion content
        }}
      >

        {/* Add HoldingAccordionHeader for holding management - disabled in multi-select mode */}
        {holdingsFields && (
          <HoldingAccordionHeader
            fields={holdingsFields}
            index={index}
            controlledVocab={controlledVocab}
            template={template}
            isDisabledActions={false} // Individual actions always enabled
            holdingRecordId={holding.id}
            createUndoAction={createUndoAction}
            isBatchModeActive={isBatchModeActive}
          />
        )}

        {/* HoldingAccordionHeader already includes the badge, so remove this duplicate */}
      </div>
    );
  };

  /** Check if the record has multi-part (volumes) or not */
  const modeOfIssuanceIdField = useField('instanceData.modeOfIssuanceId');
  const shouldGroupByVolumes = shouldEnableVolumeManagement(
    modeOfIssuanceIdField.input.value,
    controlledVocab,
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      {/* Multi-select checkbox - positioned outside accordion like volume toolbar */}
      <div style={{ paddingTop: '8px' }}>
        <Tooltip
          text={
            <FormattedMessage
              id="ui-plugin-inventory-templates.holdingCheckbox.tooltip"
              defaultMessage="Select holding for batch operations"
            />
          }
        >
          {({ ref, ariaIds }) => (
            <div ref={ref} aria-labelledby={ariaIds.text}>
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation(); // Prevent any event bubbling
                  if (onToggleSelection) {
                    onToggleSelection(holding.id);
                  }
                }}
                onMouseDown={(e) => e.preventDefault()} // Prevent focus issues
              />
            </div>
          )}
        </Tooltip>
      </div>

      {/* Accordion takes full width */}
      <div style={{ flex: 1 }}>
        <Accordion
          key={`${holding.id}-items-list}`}
          id={`${holding.id}-items-list`}
          label={
            <h3>
              <FormattedMessage
                id="ui-plugin-inventory-templates.instanceWizard.holdingsItems.accordion.label"
                values={{
                  index: index + 1,
                  location: handleAccordionLabel(),
                }}
                defaultMessage="Holding {index} {location}"
              />
            </h3>
          }
          displayWhenOpen={renderAccordionHeader()}
          displayWhenClosed={renderAccordionHeader()}
        >
          <div style={{ padding: '0 30px' }}>
            <FieldArray
              id="items-template-page"
              name={`itemsData.${holding.id}`}
              subscription={{ value: true }}
              render={({ fields }) => {
                return (
                  <ItemsList
                    fields={fields}
                    template={template}
                    holdingsRecordId={holding.id}
                    holdingLocationId={holdingLocationId}
                    controlledVocab={controlledVocab}
                    shouldGroupByVolumes={shouldGroupByVolumes}
                    onEditItem={onEditItem}
                    isBatchModeActive={isBatchModeActive}
                    instanceStatisticalCodeIds={instanceStatisticalCodeIds}
                    onUndoStackUpdate={(
                      undoStack,
                      renderUndoHistory,
                      setShowUndoHistory,
                      executeUndo,
                    ) => {
                      if (onUndoStackUpdate) {
                        onUndoStackUpdate(
                          undoStack,
                          renderUndoHistory,
                          setShowUndoHistory,
                          holding.id,
                          executeUndo,
                        );
                      }
                    }}
                  />
                );
              }}
            />
          </div>
        </Accordion>
      </div>
    </div>
  );
}

RenderHoldingItems.propTypes = {
  index: PropTypes.number,
  template: PropTypes.object,
  holding: PropTypes.object,
  controlledVocab: PropTypes.object,
  onEditItem: PropTypes.func,
  onUndoStackUpdate: PropTypes.func,
  holdingsFields: PropTypes.object, // Add holdings fields prop
  createUndoAction: PropTypes.func, // Add undo action creator prop
  // Batch operations props
  isSelected: PropTypes.bool,
  onToggleSelection: PropTypes.func,
  isBatchModeActive: PropTypes.bool,
};

export default RenderHoldingItems;
